import { lazy, Suspense, useEffect, useState } from 'react';
import { LazySection, scrollToSection } from '@/components/LazySection';
import * as Sentry from '@sentry/react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';

// Must match reactRouterV7BrowserTracingIntegration in main.tsx (react-router-dom v7),
// otherwise transaction names stay unparameterised (/clients/123 instead of /clients/:id).
const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);
import { Toaster } from 'sonner';
import { MotionConfig } from 'motion/react';
import { AuthProvider } from '@/context/AuthContext';
import { NotificationsProvider } from '@/context/NotificationsContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';

import { ThemeProvider } from '@/context/ThemeContext';
import { ScrollToTop } from '@/components/ScrollToTop';
import { CookieConsent } from '@/components/CookieConsent';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
// Above-the-fold: eager load
import { HeroSection } from '@/components/HeroSection';
// Below-the-fold: lazy load
const InteractiveCarouselSection = lazy(() => import('@/components/InteractiveCarouselSection').then(m => ({ default: m.InteractiveCarouselSection })));
const AppShowcaseSection = lazy(() => import('@/components/AppShowcaseSection').then(m => ({ default: m.AppShowcaseSection })));
const FeaturesSection = lazy(() => import('@/components/FeaturesSection').then(m => ({ default: m.FeaturesSection })));
const ProcessSection = lazy(() => import('@/components/ProcessSection').then(m => ({ default: m.ProcessSection })));
const FAQSection = lazy(() => import('@/components/FAQSection').then(m => ({ default: m.FAQSection })));
const AuthorsSection = lazy(() => import('@/components/AuthorsSection').then(m => ({ default: m.AuthorsSection })));
const ExpertAdvisorSection = lazy(() => import('@/components/ExpertAdvisorSection').then(m => ({ default: m.ExpertAdvisorSection })));
const ClosingSection = lazy(() => import('@/components/ClosingSection').then(m => ({ default: m.ClosingSection })));
// Route pages: lazy-loaded so the landing page bundle stays small.
const LoginPage = lazy(() => import('@/pages/LoginPage').then(m => ({ default: m.LoginPage })));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })));
const AdminPage = lazy(() => import('@/pages/admin/AdminPage'));
const PricingPage = lazy(() => import('@/pages/PricingPage').then(m => ({ default: m.PricingPage })));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const PrivacyPolicyPage = lazy(() => import('@/pages/PrivacyPolicyPage').then(m => ({ default: m.PrivacyPolicyPage })));
const TermsPage = lazy(() => import('@/pages/TermsPage').then(m => ({ default: m.TermsPage })));
const CookiePolicyPage = lazy(() => import('@/pages/CookiePolicyPage').then(m => ({ default: m.CookiePolicyPage })));
const GDPRPage = lazy(() => import('@/pages/GDPRPage').then(m => ({ default: m.GDPRPage })));
const DashboardPage = lazy(() => import('@/pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const DashboardLayout = lazy(() => import('@/layouts/DashboardLayout').then(m => ({ default: m.DashboardLayout })));
const ClientsPage = lazy(() => import('@/pages/ClientsPage').then(m => ({ default: m.ClientsPage })));
const WorkoutPlansPage = lazy(() => import('@/pages/WorkoutPlansPage').then(m => ({ default: m.WorkoutPlansPage })));
const ClientDetailsPage = lazy(() => import('@/pages/ClientDetailsPage').then(m => ({ default: m.ClientDetailsPage })));
const EditWorkoutPlanPage = lazy(() => import('@/pages/EditWorkoutPlanPage').then(m => ({ default: m.EditWorkoutPlanPage })));
const CalendarPage = lazy(() => import('@/pages/CalendarPage'));
const ChatPage = lazy(() => import('@/pages/ChatPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const ReportsPage = lazy(() => import('@/pages/ReportsPage').then(m => ({ default: m.ReportsPage })));
const ExerciseDatabasePage = lazy(() => import('@/pages/ExerciseDatabasePage'));
const BlogPage = lazy(() => import('@/pages/BlogPage').then(m => ({ default: m.BlogPage })));
const BlogPostPage = lazy(() => import('@/pages/BlogPostPage').then(m => ({ default: m.BlogPostPage })));
const ContactPage = lazy(() => import('@/pages/ContactPage').then(m => ({ default: m.ContactPage })));
const ExtendPlanPage = lazy(() => import('@/pages/ExtendPlanPage').then(m => ({ default: m.ExtendPlanPage })));
const PaymentSuccessPage = lazy(() => import('@/pages/PaymentSuccessPage').then(m => ({ default: m.PaymentSuccessPage })));
const InvitePage = lazy(() => import('@/pages/InvitePage').then(m => ({ default: m.InvitePage })));
const ConfirmEmailPage = lazy(() => import('@/pages/ConfirmEmailPage').then(m => ({ default: m.ConfirmEmailPage })));
const ClientWelcomePage = lazy(() => import('@/pages/ClientWelcomePage').then(m => ({ default: m.ClientWelcomePage })));
const BecomeTrainerPage = lazy(() => import('@/pages/BecomeTrainerPage').then(m => ({ default: m.BecomeTrainerPage })));
const MobileAuthCallbackPage = lazy(() => import('@/pages/MobileAuthCallbackPage').then(m => ({ default: m.MobileAuthCallbackPage })));

function PublicCookieConsent() {
  const { pathname } = useLocation();
  return /^\/admin(?:\/|$)/.test(pathname) ? null : <CookieConsent />;
}

function HomePage() {
  // Arriving with #section: mount everything so the browser can scroll to it
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (id) scrollToSection(id);
  }, []);

  // Each section loads when it nears the viewport or the browser is idle (issue #236, F27)
  return (
    <>
      <HeroSection />
      <LazySection minHeight={700}><InteractiveCarouselSection /></LazySection>
      <LazySection minHeight={800}><AppShowcaseSection /></LazySection>
      <LazySection minHeight={900}><FeaturesSection /></LazySection>
      <LazySection minHeight={700}><ProcessSection /></LazySection>
      <LazySection minHeight={600}><AuthorsSection /></LazySection>
      <LazySection minHeight={600}><ExpertAdvisorSection /></LazySection>
      <LazySection minHeight={700}><FAQSection /></LazySection>
      <LazySection minHeight={600}><ClosingSection /></LazySection>
    </>
  );
}

/** Route chunk still loading: a quiet spinner after a short delay instead of a blank page. */
function RouteFallback() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setShow(true), 200);
    return () => window.clearTimeout(id);
  }, []);
  return (
    <div className="min-h-[60vh] flex items-center justify-center" aria-busy="true">
      {show && <div className="h-6 w-6 rounded-full border-2 border-border border-t-foreground animate-spin" />}
    </div>
  );
}

export default function App() {
  return (
    <MotionConfig reducedMotion="user">
    <ThemeProvider>

      <AuthProvider>
        <NotificationsProvider>
        <Router>
          <ScrollToTop />
          <PublicCookieConsent />
          <Toaster
            position="top-right"
            richColors
            closeButton
            toastOptions={{
              style: {
                background: 'var(--toast-bg)',
                color: 'var(--toast-color)',
                border: '1px solid var(--toast-border)',
              },
            }}
          />
          <div className="min-h-[100dvh] bg-background text-foreground relative">
            <Suspense fallback={<RouteFallback />}>
            <SentryRoutes>
              <Route path="/admin" element={<AdminPage />} />
              {/* Protected Routes */}
              <Route element={<ProtectedRoute />}>
                {/* Dashboard Layout Routes */}
                <Route element={<DashboardLayout />}>
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/dashboard/clients" element={<ClientsPage />} />
                  <Route path="/dashboard/workout-plans" element={<WorkoutPlansPage />} />
                </Route>

                {/* Standalone Dashboard Pages (Self-contained layouts) */}
                <Route path="/dashboard/workout-plan/:id" element={<EditWorkoutPlanPage />} />
                <Route path="/dashboard/calendar" element={<CalendarPage />} />
                <Route path="/dashboard/chat" element={<ChatPage />} />
                <Route path="/dashboard/settings" element={<SettingsPage />} />
                <Route path="/dashboard/reports" element={<ReportsPage />} />
                <Route path="/dashboard/reports/:clientId" element={<ReportsPage />} />
                <Route path="/dashboard/reports/:clientId/:weekNumber" element={<ReportsPage />} />
                <Route path="/dashboard/exercises" element={<ExerciseDatabasePage />} />
                <Route path="/dashboard/client-details/:id" element={<ClientDetailsPage />} />
                <Route path="/extend-plan" element={<ExtendPlanPage />} />
                <Route path="/client-welcome" element={<ClientWelcomePage />} />
                <Route path="/become-trainer" element={<BecomeTrainerPage />} />
              </Route>

              {/* Mobile OAuth callback - standalone without navbar/footer */}
              <Route path="/auth/mobile-callback" element={<MobileAuthCallbackPage />} />

              {/* Reset Password - standalone without navbar/footer (must be before wildcard) */}
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/:lang/reset-password" element={<ResetPasswordPage />} />

              {/* Public Routes */}
              <Route path="/*" element={
                <>
                  <Navbar />
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/pricing" element={<PricingPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/cookie-policy" element={<CookiePolicyPage />} />
                    <Route path="/gdpr" element={<GDPRPage />} />
                    <Route path="/blog" element={<BlogPage />} />
                    <Route path="/blog/:id" element={<BlogPostPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/payment-success" element={<PaymentSuccessPage />} />
                    <Route path="/invite" element={<InvitePage />} />
                    <Route path="/confirm-email-success" element={<ConfirmEmailPage />} />
                    <Route path="/404" element={<NotFoundPage />} />
                    <Route path="*" element={<Navigate to="/404" replace />} />

                    {/* Redirects for old routes */}
                    <Route path="/polityka-prywatnosci" element={<Navigate to="/privacy-policy" replace />} />
                    <Route path="/regulamin" element={<Navigate to="/terms" replace />} />
                    <Route path="/polityka-cookies" element={<Navigate to="/cookie-policy" replace />} />
                  </Routes>
                  <Footer />
                </>
              } />
            </SentryRoutes>
            </Suspense>
          </div>
        </Router>
        </NotificationsProvider>
      </AuthProvider>

    </ThemeProvider>
    </MotionConfig>
  );
}
